import { useEffect, useState } from "react";
import { X, Heart, ExternalLink, Copy, Check } from "lucide-react";
import { DONATE_CONFIG, buildLinks } from "@/config/donate";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

export const DonateModal = ({ open, onClose }) => {
  const [amount, setAmount] = useState(DONATE_CONFIG.suggested);
  const [custom, setCustom] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!open) return;
    track("donate_opened");
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const effectiveAmount = custom ? Number(custom) : amount;
  const links = buildLinks(effectiveAmount);

  const markDonated = () => {
    localStorage.setItem("has_donated", "1");
    localStorage.setItem("donated_at", String(Date.now()));
    track("donate_marked_complete", { amount_usd: effectiveAmount });
    toast.success("thank you. seriously.", { duration: 4000 });
    onClose();
  };

  const copyHandle = async (id, handle) => {
    await navigator.clipboard.writeText(handle);
    setCopiedId(id);
    toast.success(`${handle} copied`);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div
      data-testid="donate-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl bg-[#0a0a0a] border border-white/15 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="donate-close-btn"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 text-neutral-500 hover:text-white"
          style={{ transitionProperty: "color", transitionDuration: "150ms" }}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-8 border-b border-white/10">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">
            <Heart className="w-3 h-3" /> keep the lights on
          </div>
          <h2 className="font-display font-bold text-3xl tracking-tight">
            fund s<span className="text-[color:var(--accent-amber)]">A</span><span className="text-[color:var(--accent-matrix)]">i</span>monSays<span className="text-neutral-500">.</span><span className="text-[color:var(--accent-amber)]">A</span><span className="text-[color:var(--accent-matrix)]">i</span><span className="cursor" />
          </h2>
          <p className="text-neutral-400 text-sm mt-3 max-w-lg">
            No ads, no data harvesting, no paywall. If s<span className="text-[color:var(--accent-amber)]">A</span><span className="text-[color:var(--accent-matrix)]">i</span>monSays<span className="text-neutral-500">.</span><span className="text-[color:var(--accent-amber)]">A</span><span className="text-[color:var(--accent-matrix)]">i</span> saved you time or taught you something,
            toss a few bucks toward hosting + AI credits. Every dollar keeps it free for the next person.
          </p>
          <div className="mt-4 text-[10px] text-[color:var(--accent-matrix)] uppercase tracking-widest">
            // creator identity is protected — methods below hide your real name from senders
          </div>
        </div>

        {/* Amount picker */}
        <div className="p-8 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
            // amount // suggested: ${DONATE_CONFIG.suggested}
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {DONATE_CONFIG.tiers.map((t) => (
              <button
                key={t}
                data-testid={`donate-tier-${t}`}
                onClick={() => { setAmount(t); setCustom(""); }}
                className={`px-2 py-3 text-sm font-mono border duration-150 ${
                  !custom && amount === t
                    ? "border-[color:var(--accent-amber)] bg-[color:var(--accent-amber)]/10 text-[color:var(--accent-amber)]"
                    : "border-white/10 text-neutral-300 hover:border-white/40"
                }`}
                style={{ transitionProperty: "color, border-color, background-color" }}
              >
                ${t.toLocaleString()}
              </button>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">custom $</label>
            <input
              data-testid="donate-custom-input"
              type="number"
              min="1"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="any amount"
              className="flex-1 bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none px-3 py-2 text-sm"
              style={{ transitionProperty: "border-color", transitionDuration: "150ms" }}
            />
            <div className="text-lg font-bold font-display text-[color:var(--accent-amber)]">
              ${(Number(custom) || amount || 0).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Payment methods */}
        <div className="p-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">// choose a method</div>
          {links.length === 0 ? (
            <div className="text-neutral-500 text-sm border border-dashed border-white/10 p-6">
              no payment methods configured yet. edit /app/frontend/src/config/donate.js
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {links.map((l) => (
                <div
                  key={l.id}
                  data-testid={`donate-method-${l.id}`}
                  className="border border-white/10 bg-black p-4 flex flex-col gap-2 hover:border-[color:var(--accent-amber)] duration-150 relative"
                  style={{ transitionProperty: "border-color" }}
                >
                  {l.anon && (
                    <div className="absolute top-2 right-9 text-[8px] uppercase tracking-widest text-[color:var(--accent-matrix)]">
                      anon-safe
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="font-display font-bold text-base">{l.label}</div>
                    <button
                      onClick={() => copyHandle(l.id, l.handle)}
                      data-testid={`donate-copy-${l.id}`}
                      className="text-neutral-500 hover:text-white"
                      style={{ transitionProperty: "color", transitionDuration: "150ms" }}
                    >
                      {copiedId === l.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <div className="text-xs text-neutral-400 font-mono truncate">{l.handle}</div>
                  {l.url ? (
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => track("donate_method_clicked", { method: l.id, amount_usd: effectiveAmount })}
                      data-testid={`donate-open-${l.id}`}
                      className="mt-1 inline-flex items-center justify-center gap-1.5 bg-[color:var(--accent-amber)] text-black px-3 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-white duration-150"
                      style={{ transitionProperty: "background-color" }}
                    >
                      open <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <div className="mt-1 text-[10px] uppercase tracking-widest text-neutral-500 border border-white/10 py-2 text-center">
                      copy handle → open zelle in your bank app
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-white/10">
            <p className="text-[10px] uppercase tracking-widest text-neutral-500">
              already sent? mark yourself as a supporter → nudges disappear
            </p>
            <button
              data-testid="donate-mark-btn"
              onClick={markDonated}
              className="inline-flex items-center gap-2 border border-[color:var(--accent-matrix)] text-[color:var(--accent-matrix)] hover:bg-[color:var(--accent-matrix)] hover:text-black px-4 py-2 text-[10px] uppercase tracking-widest font-bold duration-150"
              style={{ transitionProperty: "background-color, color" }}
            >
              <Check className="w-3.5 h-3.5" /> i donated
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
